import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';

const TEXT_FIELDS = [
  { key: 'legalName', label: 'Legal name', hint: 'From the company record. Change only if it changes.' },
  { key: 'abn', label: 'ABN' },
  { key: 'businessDescription', label: 'What the company does (clause 1.1)', hint: 'Completes the sentence "The Employer … in Queensland, Australia." For example: "operates an aged care service".', wide: true },
  { key: 'signatoryName', label: 'Signatory name', hint: 'The person who signs every contract for the company.' },
  { key: 'signatoryPosition', label: 'Signatory position', hint: 'For example: Director.' },
  { key: 'reportsTo', label: 'Employees report to (clause 2.3)', hint: 'Blank = the signatory.' },
  { key: 'positionDescriptionUrl', label: 'Position description link (Schedule A)', hint: 'The full web address of the position description page.', wide: true },
];

const Settings = () => {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ ok: '', err: '' });
  const [file, setFile] = useState(null);

  const apply = (data) => { setS(data); setForm({ ...data.employer }); };
  useEffect(() => { api('/admin/settings', { auth: true }).then((r) => apply(r.data)).catch((e) => setMsg({ ok: '', err: e.message })); }, []);

  const save = async () => {
    setBusy(true);
    setMsg({ ok: '', err: '' });
    try {
      const employer = {};
      TEXT_FIELDS.forEach((f) => { employer[f.key] = form[f.key] || ''; });
      employer.state = form.state;
      employer.confirmsApprovedProvider = !!form.confirmsApprovedProvider;
      employer.confirmsAcilaParty = !!form.confirmsAcilaParty;
      const r = await api('/admin/settings', { method: 'POST', auth: true, body: { employer } });
      apply(r.data);
      setMsg({ ok: 'Saved. Contracts not yet signed use these details from now on.', err: '' });
    } catch (e) {
      setMsg({ ok: '', err: e.message });
    } finally {
      setBusy(false);
    }
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setMsg({ ok: '', err: '' });
    try {
      const fd = new FormData();
      fd.append('signature', file);
      const r = await api('/admin/settings/signature', { method: 'POST', auth: true, form: fd });
      apply(r.data);
      setFile(null);
      setMsg({ ok: 'Signature uploaded. It will be stamped on contracts signed from now on.', err: '' });
    } catch (e) {
      setMsg({ ok: '', err: e.message });
    } finally {
      setBusy(false);
    }
  };

  const viewSignature = async () => {
    try {
      const r = await api('/admin/settings/signature', { auth: true });
      window.open(r.data.url, '_blank', 'noopener');
    } catch (e) { setMsg({ ok: '', err: e.message }); }
  };

  if (!s) return msg.err ? <p className="rounded-lg bg-danger-soft p-3 text-sm text-danger">{msg.err}</p> : <p className="text-muted">Loading…</p>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Contract settings</h1>
        <p className="mt-1 text-sm text-muted">These details appear in every employment contract. A contract can't be signed until the required ones are filled in.</p>
      </div>
      {msg.ok && <p className="rounded-lg bg-success-soft p-3 text-sm text-success">{msg.ok}</p>}
      {msg.err && <p className="rounded-lg bg-danger-soft p-3 text-sm text-danger">{msg.err}</p>}

      <section className="wc-card grid gap-4 p-5 sm:grid-cols-2">
        {TEXT_FIELDS.map((f) => (
          <div key={f.key} className={f.wide ? 'sm:col-span-2' : ''}>
            <label className="wc-label" htmlFor={f.key}>{f.label}</label>
            <input id={f.key} className="wc-input" value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
            {f.hint && <p className="mt-1 text-xs text-muted">{f.hint}</p>}
          </div>
        ))}
        <div>
          <label className="wc-label" htmlFor="state">State (governing law, clause 18)</label>
          <select id="state" className="wc-input" value={form.state || ''} onChange={(e) => setForm({ ...form, state: e.target.value })}>
            {s.states.map((st) => <option key={st} value={st}>{st}</option>)}
          </select>
        </div>
      </section>

      <section className="wc-card space-y-3 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-primary">Statements in clause 1.3</h2>
        <p className="text-sm text-muted">Each is printed in the contract only if ticked. Tick only what is true for the company.</p>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={!!form.confirmsApprovedProvider} onChange={(e) => setForm({ ...form, confirmsApprovedProvider: e.target.checked })} />
          <span>The company is an approved aged care provider under the Aged Care Act 1997 (Cth).</span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={!!form.confirmsAcilaParty} onChange={(e) => setForm({ ...form, confirmsAcilaParty: e.target.checked })} />
          <span>The company is a party to the Aged Care Industry Labour Agreement (ACILA) with the Department of Home Affairs.</span>
        </label>
      </section>

      <button type="button" className="wc-btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save settings'}</button>

      <section className="wc-card space-y-3 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-primary">Signatory's signature (fallback)</h2>
        <p className="text-sm text-muted">
          The signature normally arrives with each contract from Ivipass, set on Ivipass's employer record. This one is
          used only for a contract that arrives without one.{' '}
          {s.signature.uploaded
            ? `Uploaded ${new Date(s.signature.uploadedAt).toLocaleString('en-AU')}.`
            : 'None uploaded here.'}
          {' '}PNG or JPEG, up to 1 MB, ideally on a white or transparent background.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input type="file" accept="image/png,image/jpeg" onChange={(e) => setFile(e.target.files && e.target.files[0])} className="text-sm" />
          <button type="button" className="wc-btn-primary" disabled={!file || busy} onClick={upload}>Upload</button>
          {s.signature.uploaded && <button type="button" className="wc-btn-ghost" onClick={viewSignature}>View current</button>}
        </div>
      </section>
    </div>
  );
};

export default Settings;
